// Apelurile de rețea ale ecranelor de structură.
//
//   * baza e `/api` pe control plane, care PROXY-ază byte-identic citirile
//     către serviciul de structură: `GET /api/projects/{id}/structure/<rest>`;
//   * id-ul de proiect e UUID-ul platformei;
//   * citirile de pe spațiul unei soluții (sau al unui corp) se fac explicit,
//     `GET /api/projects/{id}/structure/clone/{cloneId}/<rest>` — de aici
//     parametrul opțional `cloneId` al funcțiilor de citire;
//   * catalogurile statice (verificari/marimi/pasi/lant) stau sub
//     `/api/structure/catalog/…`;
//   * transportul e cel al platformei (ui/src/api.ts): fetch cu
//     `credentials: "same-origin"`, erori ca `ApiError` cu `.status`
//     („404 = spațiul a fost șters").
//
// Pornirile (joburi pe fază), resetul și CRUD-ul de proiecte și fișiere sunt
// ale platformei (`startJob` și restul din ui/src/api.ts, `nav/actiuni`);
// ruta vizualizatorului de desene e în legaturi.ts.

import { ApiError, del, get, post, put, patch } from "../api";
import { limba } from "../i18n";
import type { SolutieRand } from "../nav/api";
import type { ContextEditor, Geometrie, VerdictAmplasare } from "./editorGeometrie";
import type { PlacaNivel } from "./editorPlaci";
import type { Simetrie } from "./editorSimetrie";
import type { ArmaturaRezumat } from "./armatura3d";
import type { DictionarRezultate } from "./texteRezultat";
import type { PaginaVerificari, SumarVerificari } from "./verificari";
import type {
  CatalogMarimi,
  CatalogPasi,
  CatalogVerificari,
  DrawingData,
  EstimareStructura,
  ExtrasArmare,
  Prefs,
  RaportLant,
  Reglaje,
  ReteteConfig,
  ReteteResponse,
  StepRezumat,
  StepState,
  ZonaInteres,
} from "./tipuri";

// Rădăcina rutelor de structură ale unui proiect; cu `cloneId` se citește
// clona unei soluții (vechiul „același drum, alt id numeric de proiect").
function baza(projectId: string, cloneId?: string | null): string {
  return cloneId != null
    ? `/api/projects/${projectId}/structure/clone/${cloneId}`
    : `/api/projects/${projectId}/structure`;
}

// --- editorul soluției (contract §5.4) ---------------------------------------
//
// Editorul se deschide pe orice rând de structură gata — S0, M sau D —, care
// rămâne SURSA și nu se schimbă. Ciorna stă în interfață; serverul doar o
// verifică (`verificaEditare`) până la «Salvează ca Soluția N+1», care naște
// M_{N+1} (geometria omului e macro-ul ei, D10; Pasul 4 i se scrie în fundal).
// Calculul de detaliu îl pornește omul. Rândul nou se naște LA SALVARE, nu la
// «Editează»: nicio stare «în editare», nicio gaură în numerotare la renunțare.

/** Geometria și contextul unui corp al sursei. `cod` null = soluția n-are
 *  corpuri. */
export type EditorCorp = {
  cod: string | null;
  /** corpul n-are Pasul 4 (sărit, sau căzut): motivul — nu se poate edita,
   *  iar geometria vine goală și contextul lipsește */
  indisponibil?: string;
  /** `geometrie_finala` a sursei; la D, cea moștenită de la M */
  geometrie: Geometrie;
  context: ContextEditor | null;
  /** id-urile blocate deja în sursă */
  blocate: string[];
  erori: string[];
  /** gărzile generatorului sunt, pentru om, doar avertismente (plan §3) */
  avertismente: string[];
  /** perechi (b, L) distincte (D8) */
  tipuri_esv: number;
  /** ce refuză verdictele de amplasare ale Pasului 4 pe geometria de plecare
   *  (`editare.verdictele_amplasarii`); null = nu s-au putut calcula */
  amplasare?: VerdictAmplasare[] | null;
  /** placa și ochiurile fiecărui nivel, ale FEM-ului, pe geometria de
   *  plecare (`editare.placile_editorului`); null = nu s-au putut calcula */
  placi?: PlacaNivel[] | null;
  /** orbitele de simetrie ale geometriei de plecare (`geometrie/orbite.py`):
   *  gemenii unui element urmează în editor mutarea și secțiunea lui */
  simetrie?: Simetrie | null;
};
export type EditorDeschis = {
  sursa: { id: string; faza: "schita" | "macro" | "detaliu"; numar: number;
           sursa: "generat" | "om" | "structurist" };
  arhitectura: string;
  /** N+1, pentru «Salvează ca Soluția N+1»; se fixează la salvare */
  numar_nou: number;
  /** numele ciornei: rădăcina și numărul editării (`numeleEditarii`) */
  editarea?: { radacina: number; nr: number } | Record<string, never>;
  corpuri: EditorCorp[];
};
export type VerificareEditare = {
  erori: string[]; avertismente: string[]; tipuri_esv: number;
  /** verdictele de amplasare pe geometria trimisă (null: geometrie cu greșeli,
   *  sau Pasul 4 n-a mers pe ea) */
  amplasare?: VerdictAmplasare[] | null;
  /** placa și ochiurile pe geometria trimisă, ca la deschidere */
  placi?: PlacaNivel[] | null;
};

/** Deschiderile editorului, ținute puțin: serverul reface arhitectura din
 *  documentul canonic pe fiecare corp, deci deschiderea durează secunde. Ea se
 *  cere dinainte (`preincarcaEditorul` — cât privești soluția, sau când treci
 *  cu mouse-ul peste «Editează»), iar clicul găsește răspunsul gata. Sursa e
 *  un rând gata, care nu se mai schimbă; numele ciornei (`editarea`) da, la
 *  fiecare salvare — de aceea salvarea uită tot proiectul (`uitaEditorul`). */
const EDITOR_TTL_MS = 5 * 60_000;
const editoare = new Map<string, { la: number; p: Promise<EditorDeschis> }>();

export function deschideEditor(projectId: string, sursa: string): Promise<EditorDeschis> {
  const cheie = `${projectId}|${sursa}`;
  const tinut = editoare.get(cheie);
  if (tinut && Date.now() - tinut.la < EDITOR_TTL_MS) return tinut.p;
  const p = get<EditorDeschis>(`/api/projects/${projectId}/solutions/${sursa}/editare`,
                               { silent: true });
  editoare.set(cheie, { la: Date.now(), p });
  p.catch(() => { if (editoare.get(cheie)?.p === p) editoare.delete(cheie); });
  return p;
}

/** Cere deschiderea dinainte, fără să aștepte și fără să anunțe o cădere. */
export function preincarcaEditorul(projectId: string, sursa: string): void {
  deschideEditor(projectId, sursa).catch(() => undefined);
}

/** Uită deschiderile ținute ale proiectului. */
export function uitaEditorul(projectId: string): void {
  for (const k of [...editoare.keys()]) if (k.startsWith(`${projectId}|`)) editoare.delete(k);
}

/** Verifică geometria unui corp, fără să scrie nimic. */
export function verificaEditare(projectId: string, sursa: string,
                                corp: string | null, geometrie: Geometrie):
    Promise<VerificareEditare> {
  return post(`/api/projects/${projectId}/solutions/${sursa}/editare/verifica`,
              { corp, geometrie });
}

/** «Salvează ca Soluția N+1»: 201 cu rândul nou. Pasul 4 al lui se scrie în
 *  fundal, iar calculul de detaliu nu pornește: îl cere omul. */
export function salveazaCaSolutieNoua(projectId: string, sursa: string,
                                      corpuri: { cod: string | null; geometrie: Geometrie }[]):
    Promise<{ macro: SolutieRand; blocate: number }> {
  return post<{ macro: SolutieRand; blocate: number }>(
    `/api/projects/${projectId}/solutions/${sursa}/editare`, { corpuri })
    .then((r) => { uitaEditorul(projectId); return r; });
}

// --- pași --------------------------------------------------------------------

// Starea pașilor FĂRĂ rezultate — ce se cere de două ori pe secundă.
export function listStepsRezumat(
  projectId: string,
  cloneId?: string | null
): Promise<Record<string, StepRezumat>> {
  return get(`${baza(projectId, cloneId)}/steps?rezumat=1`, { silent: true });
}

// Rezultatul unui singur pas: se aduce doar când `updated_at` s-a schimbat.
export function getStep(
  projectId: string,
  step: string,
  cloneId?: string | null,
  // `bp` cere o stare ÎNGHEȚATĂ din istoricul Pasului 4. Subpașii aveau până
  // câte un spațiu de lucru;
  // acum sunt o parte a rezultatului pasului, deci se cer de la el.
  bp?: string | null
): Promise<StepState> {
  const q = bp ? `?bp=${encodeURIComponent(bp)}` : "";
  // hidratare de fundal (v. StructureContext): fără toast la eșec
  return get(`${baza(projectId, cloneId)}/steps/${step}${q}`, { silent: true });
}

/** SUMARUL tabloului de verificări al spațiului: câte verificări are fiecare
 *  tip pe fiecare verdict, fără rândurile lor (`structure/tablou_verificari.
 *  py`). Câțiva kilobytes în locul rezultatelor celor 13 pași din care se
 *  adună; serverul răspunde 304 cât timp pașii nu s-au schimbat. */
export function getVerificariSumar(projectId: string,
                                   cloneId: string): Promise<SumarVerificari> {
  return get(`${baza(projectId, cloneId)}/verificari`, { silent: true });
}

/** O pagină din rândurile unui card al tabloului, cerută la deschiderea lui:
 *  `grup` e cheia cardului, `verdicte` filtrul ecranului (gol = toate). */
export function getVerificariRanduri(
  projectId: string,
  cloneId: string,
  q: { grup: string; tematica: string | null; verdicte: readonly string[];
       inceput: number; cate: number }
): Promise<PaginaVerificari> {
  const p = new URLSearchParams({
    grup: q.grup, tematica: q.tematica ?? "", verdicte: q.verdicte.join(","),
    inceput: String(q.inceput), cate: String(q.cate),
  });
  return get(`${baza(projectId, cloneId)}/verificari/randuri?${p}`, { silent: true });
}

/** Formele modale ale Pasului 7, cerute separat de rezultat (v.
 *  `forme_separat`): sunt megaocteți și se desenează numai în tabul «Moduri».
 *  Ținute în memorie pe spațiu și versiune, ca tabul să se deschidă a doua
 *  oară fără nicio cerere, și ca Studio-ul să le poată aduce din timp. */
export type FormeModale = { moduri: { mod: number; forma: number[][] }[]; updated_at?: string };
const forme = new Map<string, Promise<FormeModale>>();

export function getFormeModale(
  projectId: string, cloneId?: string | null, versiune?: string | null,
): Promise<FormeModale> {
  const cheie = `${projectId}|${cloneId ?? ""}|${versiune ?? ""}`;
  const gata = forme.get(cheie);
  if (gata) return gata;
  const p = get<FormeModale>(`${baza(projectId, cloneId)}/steps/model_fem/forme`,
                            { silent: true })
    .catch((e) => { forme.delete(cheie); throw e; });
  forme.set(cheie, p);
  if (forme.size > 6) forme.delete(forme.keys().next().value!);
  return p;
}

export function preincarcaForme(
  projectId: string, cloneId?: string | null, versiune?: string | null,
): void {
  getFormeModale(projectId, cloneId, versiune).catch(() => {});
}

/** ARMĂTURA PENTRU DESEN — rezumatul Pașilor 13–16 (v. `armare_3d.py`).
 *
 *  Se cere NUMAI din layerul «Realist» al scenei, fiindcă numai el o
 *  desenează: în «Clasic» nu se atinge rețeaua. Rezumatul e sub 30 KB (față
 *  de ~3,7 MB cât au rezultatele întregi), deci nu întârzie primul cadru;
 *  se ține pe (spațiu, versiune), ca întoarcerea la Studio să nu-l ceară din
 *  nou, și cel mult patru, ca memoria filei să nu crească la fiecare soluție
 *  deschisă. */
const armaturi = new Map<string, Promise<ArmaturaRezumat>>();

export function getArmatura3d(
  projectId: string, cloneId?: string | null, versiune?: string | null,
): Promise<ArmaturaRezumat> {
  const cheie = `${projectId}|${cloneId ?? ""}|${versiune ?? ""}`;
  const gata = armaturi.get(cheie);
  if (gata) return gata;
  const p = get<ArmaturaRezumat>(
    `${baza(projectId, cloneId)}/steps/model_fem/armatura`, { silent: true })
    .catch((e) => { armaturi.delete(cheie); throw e; });
  armaturi.set(cheie, p);
  if (armaturi.size > 4) armaturi.delete(armaturi.keys().next().value!);
  return p;
}

/** MODELUL 3D FĂRĂ ANALIZĂ — geometria Pasului 7, ca structura să se poată
 *  vedea înainte ca Pasul 7 să ruleze (o SCHIȚĂ de structură, sau un lanț
 *  care abia a trecut de Pasul 4).
 *
 *  O CITIRE NU CALCULEAZĂ: serverul răspunde din cache sau cu
 *  `{se_construieste: true}` (202) și construiește în fundal — construcția
 *  ține 5–30 s pe un corp mare, iar citirile au 15 s de răbdare prin control
 *  plane, deci pe drumul cererii ieșea 503 exact pe corpurile pentru care
 *  există. Aici se ține pe spațiu, ca întoarcerea la Studio să nu ceară din
 *  nou; un 202 NU se ține minte, fiindcă răspunsul următor e altul.
 *
 *  `409` = Pasul 4 n-a rulat, sau Pasul 7 ARE rezultat (se citește pasul). */
const schite = new Map<string, Promise<FemSchita>>();
export type FemSchita = { schita?: true; complex?: true; model?: unknown;
                          se_construieste?: true };

export function getSchitaFem(
  projectId: string, cloneId?: string | null, versiune?: string | null,
): Promise<FemSchita> {
  const cheie = `${projectId}|${cloneId ?? ""}|${versiune ?? ""}`;
  const gata = schite.get(cheie);
  if (gata) return gata;
  const p = get<FemSchita>(`${baza(projectId, cloneId)}/steps/model_fem/schita`,
                           { silent: true })
    .then((r) => {
      // se construiește: nu se ține minte, ca reîncercarea să ceară din nou
      if (r?.se_construieste) schite.delete(cheie);
      return r;
    })
    .catch((e) => { schite.delete(cheie); throw e; });
  schite.set(cheie, p);
  if (schite.size > 4) schite.delete(schite.keys().next().value!);
  return p;
}

// --- vederea întregului complex («Toate corpurile») ---------------------------
// Același protocol ca schița: 200 din cache, 202 «se construiește» (ecranul
// revine peste câteva secunde), 409 pe o soluție fără corpuri sau pe care
// niciun corp n-a ajuns la Pasul 4. Se cere pe SPAȚIUL SOLUȚIEI, nu pe al
// unui corp: corpurile se pun împreună pe server (`complex_corpuri.py`).
const complexe = new Map<string, Promise<FemSchita>>();

export function getModelComplex(
  projectId: string, solutie: string, versiune?: string | null,
): Promise<FemSchita> {
  const cheie = `${projectId}|${solutie}|${versiune ?? ""}`;
  const gata = complexe.get(cheie);
  if (gata) return gata;
  const p = get<FemSchita>(`${baza(projectId, solutie)}/steps/model_fem/complex`,
                           { silent: true })
    .then((r) => {
      if (r?.se_construieste) complexe.delete(cheie);
      return r;
    })
    .catch((e) => { complexe.delete(cheie); throw e; });
  complexe.set(cheie, p);
  if (complexe.size > 4) complexe.delete(complexe.keys().next().value!);
  return p;
}

export function stepDrawingUrl(
  projectId: string,
  step: string,
  nivel?: number,
  cloneId?: string | null,
  // parametri de VEDERE ai desenului (azi: `fundatie=1`, care aprinde layerele
  // fundației pe planul nivelului de jos)
  vedere?: Record<string, string>
): string {
  const q = new URLSearchParams(vedere ?? {});
  if (nivel != null) q.set("nivel", String(nivel));
  const s = q.toString();
  return `${baza(projectId, cloneId)}/steps/${step}/drawing${s ? `?${s}` : ""}`;
}

/** Raportul lanțului, pe spațiul unui rând (contract §6: pe proiect au rămas
 *  doar setările). */
export function getLant(projectId: string, cloneId: string): Promise<RaportLant> {
  return get(`${baza(projectId, cloneId)}/lant`, { silent: true });
}

// --- desene ------------------------------------------------------------------

/** Desenele deja aduse, pe adresă și versiune. Un desen de nivel are sute de
 *  KB și se interpretează pe server la fiecare cerere; ținut aici, clicul pe
 *  «2D» după ce modelul 3D s-a afișat îl găsește gata (v. `preincarcaDesen`),
 *  iar întoarcerea la un nivel văzut nu-l mai cere. Versiunea e `updated_at`
 *  al pasului care l-a desenat: un pas rerulat dă alt desen la aceeași adresă. */
const desene = new Map<string, Promise<DrawingData>>();
const DESENE_MAX = 24;

export function fetchDrawingUrl(url: string, versiune?: string | null): Promise<DrawingData> {
  const cheie = `${versiune ?? ""}|${url}`;
  const gata = desene.get(cheie);
  if (gata) return gata;
  const p = get<DrawingData>(url).catch((e) => {
    desene.delete(cheie);            // un eșec nu se ține minte
    throw e;
  });
  desene.set(cheie, p);
  if (desene.size > DESENE_MAX) desene.delete(desene.keys().next().value!);
  return p;
}

/** Aduce un desen în memorie, pentru mai târziu. Eșecul e tăcut: e o
 *  anticipare, nu o cerere a omului. */
export function preincarcaDesen(url: string, versiune?: string | null): void {
  fetchDrawingUrl(url, versiune).catch(() => {});
}

// --- planul de armare (colile setului, desenele, DXF / DWG) ------------------
//
// Colile se desenează pe server din rezultatele pașilor de armare
// (`structure/planse/set.py`) și se țin pe VERSIUNEA setului: `versiune` din
// `/coli` e cheia memoriei desenelor din browser (`fetchDrawingUrl`).

export type FamilieCoala = "grinzi" | "esv" | "fundatie" | "placa" | "cofraj" | "sapatura";
export type ColaPlanArmare = {
  id: string;
  familie: FamilieCoala;
  titlu: string;
  nivel: string | null;
  /** id-urile de plan ale elementelor de pe coală (GR1.3, ST7.2, «Ax 1», OP3.2) */
  elemente: string[];
};
export type PlanArmareColi = {
  versiune: string;
  coli: ColaPlanArmare[];
  /** convertorul DXF → DWG e instalat pe server */
  dwg_disponibil: boolean;
};

/** Raportul conversiei DXF → DWG (antetul `X-OB-DWG-Raport`): câte entități
 *  au intrat, câte au ieșit la recitire și câte s-au pierdut. */
export type RaportDwg = {
  ok: boolean;
  entitati_dxf: number;
  entitati_dwg: number;
  pierdute: number;
  mesaj: string;
};

const PLAN_ARMARE = "steps/plan_armare";

export function getPlanArmareColi(projectId: string, cloneId?: string | null):
    Promise<PlanArmareColi> {
  return get(`${baza(projectId, cloneId)}/${PLAN_ARMARE}/coli`, { silent: true });
}

/** Desenul unei coli; `evidentiat` aprinde un element pe ea. */
export function planArmareDesenUrl(projectId: string, cloneId: string | null | undefined,
                                   coala: string, evidentiat?: string | null): string {
  const q = new URLSearchParams({ coala });
  if (evidentiat) q.set("evidentiat", evidentiat);
  return `${baza(projectId, cloneId)}/${PLAN_ARMARE}/desen?${q.toString()}`;
}

/** Blocul propriu al unui element de pe coala lui (răspunsul poartă și
 *  `coala`, id-ul colii din care face parte). */
export function planArmareElementUrl(projectId: string, cloneId: string | null | undefined,
                                     ref: string): string {
  return `${baza(projectId, cloneId)}/${PLAN_ARMARE}/element?ref=${encodeURIComponent(ref)}`;
}

/** Forma cotată a unei mărci de bară / etrier. */
export function planArmareMarcaUrl(projectId: string, cloneId: string | null | undefined,
                                   marca: string): string {
  return `${baza(projectId, cloneId)}/${PLAN_ARMARE}/marca?marca=${encodeURIComponent(marca)}`;
}

/** Ce se descarcă: o coală (id-ul ei, `"tot"` = setul întreg) sau blocul
 *  unui element — desenul paginii lui. */
export type TintaDescarcare = string | { element: string };

/** Fișierul DXF / DWG al unei coli, al setului întreg (`coala = "tot"`) sau
 *  al blocului unui element (`{ element: ref }`). */
export function planArmareFisierUrl(projectId: string, cloneId: string | null | undefined,
                                    fel: "dxf" | "dwg", tinta: TintaDescarcare): string {
  const q = typeof tinta === "string"
    ? `coala=${encodeURIComponent(tinta)}`
    : `element=${encodeURIComponent(tinta.element)}`;
  return `${baza(projectId, cloneId)}/${PLAN_ARMARE}/${fel}?${q}`;
}

/** Descarcă fișierul DXF / DWG al unei coli în browser (cu cookie-ul de
 *  sesiune, ca orice cerere). Un 409 e conversia DWG căzută sau lipsă:
 *  `ApiError` cu `code = "dwg"` și `date.raport.mesaj` de arătat. La reușită
 *  întoarce raportul conversiei, când serverul l-a trimis (DWG). */
export async function descarcaPlanArmare(
  projectId: string, cloneId: string | null | undefined,
  fel: "dxf" | "dwg", tinta: TintaDescarcare,
): Promise<{ raport: RaportDwg | null }> {
  const url = planArmareFisierUrl(projectId, cloneId, fel, tinta);
  return descarcaFisierPlansa(url, `plan-armare.${fel}`);
}

/** Descărcarea propriu-zisă a unui fișier de planșă (DXF / DWG), comună
 *  planului de armare și celui de cofraj: cererea cu cookie-ul de sesiune,
 *  numele din `Content-Disposition`, raportul conversiei DWG din antet. */
async function descarcaFisierPlansa(url: string, numeImplicit: string):
    Promise<{ raport: RaportDwg | null }> {
  const r = await fetch(url, { method: "GET", credentials: "same-origin" });
  if (!r.ok) {
    const payload = await r.json().catch(() => null);
    throw new ApiError(r.status, payload?.error ?? "unknown",
                       payload?.raport?.mesaj ?? payload?.message ?? r.statusText,
                       payload ?? undefined);
  }
  const nume = numeDinContentDisposition(r.headers.get("Content-Disposition")) ?? numeImplicit;
  const blob = await r.blob();
  salveazaBlob(blob, nume);
  let raport: RaportDwg | null = null;
  const antet = r.headers.get("X-OB-DWG-Raport");
  if (antet) {
    try { raport = JSON.parse(antet) as RaportDwg; } catch { raport = null; }
  }
  return { raport };
}

// --- planul de cofraj (o coală pe nivel, din modelul Pasului 7) ----------------
//
// Aceeași mecanică ca planul de armare (`structure/planse/cofraj.py`): colile
// vin din `/coli` cu versiunea setului, desenul unei coli din `/desen`, DXF-ul
// și DWG-ul din `/dxf` și `/dwg` (`coala = "tot"` = setul întreg).

const PLAN_COFRAJ = "steps/plan_cofraj";

export function getPlanCofrajColi(projectId: string, cloneId?: string | null):
    Promise<PlanArmareColi> {
  return get(`${baza(projectId, cloneId)}/${PLAN_COFRAJ}/coli`, { silent: true });
}

/** Desenul unei coli de cofraj; `evidentiat` aprinde un element pe ea. */
export function planCofrajDesenUrl(projectId: string, cloneId: string | null | undefined,
                                   coala: string, evidentiat?: string | null): string {
  const q = new URLSearchParams({ coala });
  if (evidentiat) q.set("evidentiat", evidentiat);
  return `${baza(projectId, cloneId)}/${PLAN_COFRAJ}/desen?${q.toString()}`;
}

/** Fișierul DXF / DWG al unei coli de cofraj sau al setului (`coala = "tot"`). */
export function planCofrajFisierUrl(projectId: string, cloneId: string | null | undefined,
                                    fel: "dxf" | "dwg", coala: string): string {
  return `${baza(projectId, cloneId)}/${PLAN_COFRAJ}/${fel}?coala=${encodeURIComponent(coala)}`;
}

/** Descarcă fișierul DXF / DWG al unei coli de cofraj, ca `descarcaPlanArmare`. */
export async function descarcaPlanCofraj(
  projectId: string, cloneId: string | null | undefined,
  fel: "dxf" | "dwg", coala: string,
): Promise<{ raport: RaportDwg | null }> {
  const url = planCofrajFisierUrl(projectId, cloneId, fel, coala);
  return descarcaFisierPlansa(url, `plan-cofraj.${fel}`);
}

// --- planul de săpătură (planul cu secțiunile rabatate și coala secțiunilor) --
//
// Aceeași mecanică (`structure/planse/sapatura.py`): colile din `/coli`, desenul
// din `/desen`, DXF-ul și DWG-ul din `/dxf` și `/dwg` (`coala = "tot"` = setul).

const PLAN_SAPATURA = "steps/plan_sapatura";

export function getPlanSapaturaColi(projectId: string, cloneId?: string | null):
    Promise<PlanArmareColi> {
  return get(`${baza(projectId, cloneId)}/${PLAN_SAPATURA}/coli`, { silent: true });
}

/** Desenul unei coli de săpătură; `evidentiat` aprinde un șanț pe ea. */
export function planSapaturaDesenUrl(projectId: string, cloneId: string | null | undefined,
                                     coala: string, evidentiat?: string | null): string {
  const q = new URLSearchParams({ coala });
  if (evidentiat) q.set("evidentiat", evidentiat);
  return `${baza(projectId, cloneId)}/${PLAN_SAPATURA}/desen?${q.toString()}`;
}

/** Fișierul DXF / DWG al unei coli de săpătură sau al setului (`coala = "tot"`). */
export function planSapaturaFisierUrl(projectId: string, cloneId: string | null | undefined,
                                      fel: "dxf" | "dwg", coala: string): string {
  return `${baza(projectId, cloneId)}/${PLAN_SAPATURA}/${fel}?coala=${encodeURIComponent(coala)}`;
}

/** Descarcă fișierul DXF / DWG al unei coli de săpătură, ca `descarcaPlanArmare`. */
export async function descarcaPlanSapatura(
  projectId: string, cloneId: string | null | undefined,
  fel: "dxf" | "dwg", coala: string,
): Promise<{ raport: RaportDwg | null }> {
  const url = planSapaturaFisierUrl(projectId, cloneId, fel, coala);
  return descarcaFisierPlansa(url, `plan-sapatura.${fel}`);
}

/** Numele fișierului din `attachment; filename="…"`. */
export function numeDinContentDisposition(antet: string | null): string | null {
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(antet ?? "");
  return m ? decodeURIComponent(m[1].trim()) : null;
}

function salveazaBlob(blob: Blob, nume: string): void {
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = u;
  a.download = nume;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // adresa se retrage după ce browserul a apucat s-o deschidă
  setTimeout(() => URL.revokeObjectURL(u), 10_000);
}

// desenul-sursă al unui fișier DWG (vechiul GET /api/files/{fid}/drawing)
export function fileDrawingUrl(projectId: string, fileId: string): string {
  return `${baza(projectId)}/files/${fileId}/drawing`;
}

// descărcarea documentului-sursă (vechiul GET /api/files/{fid}/download);
// fațada înaintează `files/{uuid}/download` către serviciul de structură,
// care răspunde cu octeții fișierului — `fileId` e UUID-ul platformei
export function fileDownloadUrl(projectId: string, fileId: string): string {
  return `${baza(projectId)}/files/${fileId}/download`;
}

// zona de interes a unui plan cu mai multe clădiri (vechiul
// PATCH /api/files/{fid} {zona_interes}) — restul câmpurilor fișierului
// (categorie, folder) se editează prin `patchFile` din ui/src/api.ts
export function updateFileZone(
  projectId: string,
  fileId: string,
  zone: ZonaInteres[] | null
): Promise<unknown> {
  return patch(`/api/projects/${projectId}/files/${fileId}/zone`, { zone });
}

// --- preferințe, reglaje, modificări ------------------------------------------

export function getPrefs(
  projectId: string,
  cloneId?: string | null
): Promise<Prefs> {
  return get(`${baza(projectId, cloneId)}/prefs`);
}

/** Scrie preferințele. Se poate trimite doar FELIA pe care o stăpânește
 *  ecranul: endpointul face merge peste rândul existent, iar o trimitere
 *  completă ar transforma toate implicitele în „valori puse de om". */
export function savePrefs(
  projectId: string,
  prefs: Partial<Prefs>
): Promise<Prefs> {
  return put(`${baza(projectId)}/prefs`, prefs);
}

export function getReglaje(
  projectId: string,
  cloneId?: string | null
): Promise<Reglaje> {
  return get(`${baza(projectId, cloneId)}/reglaje`);
}

export function stergeReglaje(projectId: string): Promise<unknown> {
  return del(`${baza(projectId)}/reglaje`);
}

// --- rețete, deviz, extras -----------------------------------------------------

export function getRetete(
  projectId: string,
  cloneId?: string | null
): Promise<ReteteResponse> {
  return get(`${baza(projectId, cloneId)}/retete`);
}

export function saveRetete(
  projectId: string,
  config: ReteteConfig | null
): Promise<ReteteResponse> {
  return put(`${baza(projectId)}/retete`, { config });
}

// `rezumat`: devizul asamblat al unei soluții cu corpuri vine cu corpurile fără
// articolele și detaliile lor mari (cantități, componente, extras, plan de
// cofrare) — megaocteți pe fiecare corp, pe care pagina de costuri nu-i citește
export function getEstimare(
  projectId: string,
  cloneId?: string | null,
  { rezumat = false }: { rezumat?: boolean } = {}
): Promise<EstimareStructura> {
  return get(`${baza(projectId, cloneId)}/retete/estimare${rezumat ? "?rezumat=1" : ""}`);
}

// extrasul de armătură, poziție cu poziție — se cere separat de deviz fiindcă
// la un bloc sunt mii de poziții
export function getExtrasArmare(
  projectId: string,
  // `stack` = STIVA unui stâlp („ST11" prinde ST11.1, ST11.2, … și mustățile
  // lor). `element` compară pe egalitate exactă, deci nu poate face asta.
  filtru: { element?: string; categorie?: string; stack?: string } = {},
  cloneId?: string | null
): Promise<ExtrasArmare> {
  const q = new URLSearchParams();
  if (filtru.element) q.set("element", filtru.element);
  if (filtru.stack) q.set("stack", filtru.stack);
  if (filtru.categorie) q.set("categorie", filtru.categorie);
  const qs = q.toString();
  return get(
    `${baza(projectId, cloneId)}/retete/extras${qs ? `?${qs}` : ""}`
  );
}

// --- exporturi CSV + canonic ----------------------------------------------------

export function eforturiCsvUrl(
  projectId: string,
  cloneId?: string | null
): string {
  return `${baza(projectId, cloneId)}/eforturi.csv`;
}

// în aplicația veche linkul era scris de mână în StepRezistentaVerificari
export function fundatiiCsvUrl(
  projectId: string,
  cloneId?: string | null
): string {
  return `${baza(projectId, cloneId)}/fundatii.csv`;
}

// --- cataloagele statice ---------------------------------------------------------
// Statice pe toată sesiunea, dar NU peste limbi: serverul le scrie în limba
// profilului (antetul `X-OB-Limba`), deci cache-ul e pe (catalog, limbă). Se
// cer o singură dată pe limbă, oricâte componente le folosesc; componentele le
// iau prin `useCatalog`, care le re-cere când se schimbă limba.

const cataloage = new Map<string, Promise<unknown>>();

function catalogStatic<T>(nume: string): Promise<T> {
  const cheie = `${nume}|${limba()}`;
  let p = cataloage.get(cheie);
  if (!p) {
    p = get(`/api/structure/catalog/${nume}`);
    cataloage.set(cheie, p);
  }
  return p as Promise<T>;
}

export function getCatalogMarimi(): Promise<CatalogMarimi> {
  return catalogStatic("marimi");
}

export function getCatalogPasi(): Promise<CatalogPasi> {
  return catalogStatic("pasi");
}

export function getCatalogVerificari(): Promise<CatalogVerificari> {
  return catalogStatic("verificari");
}

/** Dicționarul textelor din rezultatele pașilor (v. `texteRezultat.ts`). */
export function getCatalogRezultate(): Promise<DictionarRezultate> {
  return catalogStatic("rezultate");
}

// --- Gropile de pământ ------------------------------------------------------
//
// Unde pleacă pământul săpat. Costul evacuării nu e o cifră fixă: fiecare
// groapă are altă distanță și alt tarif, iar cele două trag în sensuri opuse.
// Serviciul le socotește pe toate; ecranul le desenează pe hartă și lasă omul
// să aleagă altfel decât automat.

export interface GroapaOptiune {
  code: string;
  name: string;
  address: string;
  lat: number;
  lon: number;
  distance_km: string;
  /** De unde vine `distance_km`: „routed" = drum măsurat pe rețeaua de șosele,
   *  „estimated" = linie dreaptă × 1,3, fiindcă routerul n-a răspuns sau e oprit.
   *  Se citește PE RÂND, nu pe tabel: un răspuns în care unele gropi sunt rutate
   *  și altele nu e o stare normală. */
  distance_source: "routed" | "estimated";
  trips: number;
  transport_cost: string;
  disposal_cost: string;
  total: string;
  chosen: boolean;
}

export interface GropiRaspuns {
  chosen: GroapaOptiune;
  /** „auto" = cea mai ieftină, aleasă de program; „user" = pusă de om. */
  chosen_by: "auto" | "user";
  options: GroapaOptiune[];
  /** Linia pe care merge camionul până la groapa aleasă, [[lat, lon], …].
   *  Lipsă sau `null` = distanța n-a putut fi rutată, deci nici traseul nu se
   *  cunoaște, iar harta desenează segmentul drept — care arată ca o aproximare
   *  fiindcă este. */
  route?: [number, number][] | null;
  site: {
    lat: number; lon: number; address: string | null;
    /** cât de exact știm unde e lotul — harta desenează zonă, nu punct, când
     *  poziția e doar geocodată dintr-o adresă vagă */
    precision?: "contur" | "punct" | "strada" | "localitate";
  };
}

// Căile încep cu `/api` — `fetchRaw` le dă lui `fetch` ca atare, nu prefixează
// nimic. Fără el, cererea nimerește `location /` din nginx, primește index.html
// cu 200, iar `response.json()` cade pe `<!doctype html>` cu un mesaj care nu
// spune nimic despre cauză („The string did not match the expected pattern").

/** Gropile socotite pentru volumul de evacuat al proiectului.
 *
 * `volum_m3` vine de pe deviz — e cantitatea pe care ecranul o are deja. Un 409
 * NU e o defecțiune: înseamnă „nu se poate alege încă", cu `reason` spunând de
 * ce (lot fără coordonate, volum zero, nicio groapă în bază). */
export function getGropi(projectId: string, volumM3: number):
    Promise<GropiRaspuns> {
  return get(`/api/projects/${projectId}/cost/gropi?volum_m3=${volumM3}`);
}

/** Alege o groapă. Nu recalculează nimic — costul se cere separat. */
export function alegeGroapa(projectId: string, code: string):
    Promise<{ ok: boolean; code: string }> {
  return post(`/api/projects/${projectId}/cost/gropi`, { code });
}
